import format from 'date-fns/format';
import produce from 'immer';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Alerts,
  Button,
  CanAccess,
  Checkbox,
  Dropdown,
  Pagination,
  Table,
  TableCell,
  TableHeader,
  TableRow,
  Text,
  Tooltip,
  WrapperFlex,
} from '@unimed-fesp/components';

import { ImPencil } from 'react-icons/im';
import { MdAdb, MdFileDownload, MdInfo } from 'react-icons/md';

import theme from '../../../styles/theme';

import { ContainerEmptyContent, HoverActions } from './styles';

import { StatusListaAPIEnum, TipoPerfilUsuarioEnum } from '../../../enums';
import { Creators as ListaActions } from '../../../store/ducks/lista';

import FadeIn from '../../atoms/FadeIn';
import Icon from '../../atoms/Icon';

import LoadingSpinning from '../../molecules/LoadingSpinning';

import {
  isAdmin,
  isContratante,
  isContratanteNestle,
} from '../../../utils/userRole';

import history from '../../../services/history';

const { REACT_APP_MOVCAD_LEGACY } = process.env;

function TabTableLista({
  setPaginationParams,
  paginationParams,
  handlePageChangeKeyDown,
  handleConfirmAction,
  handleAcaoListas,
  handleAcaoListaById,
  handleChangePage,
  reportsItems,
  setReportItems,
  handleCheckBoxAllPagesReport,
  isCheckedAllPages,
  handleDropdownItemClick,
  setIsCheckedAllPages,
  downloadFileOriginLot,
}) {
  const { usuario } = useSelector((state) => state.auth);
  const { statusLists, listas } = useSelector((state) => state.lista);
  const status = statusLists.response.actives;

  function isPendentStatusTable() {
    if (status.length === 1 && status[0].id === 10) {
      return 'Pendente';
    }
    return 'Todas';
  }

  function handleRender(listaStatus, tabName = null) {
    const tipoStatus = StatusListaAPIEnum.returnNameForTab[tabName];
    if (listas.loading) {
      return <LoadingSpinning />;
    }

    if (!!listaStatus.data && listaStatus.data.length > 0) {
      return (
        <TableLista
          pageData={listaStatus}
          listas={listaStatus.data}
          setPaginationParams={setPaginationParams}
          paginationParams={paginationParams}
          handlePageChangeKeyDown={handlePageChangeKeyDown}
          handleConfirmAction={handleConfirmAction}
          handleAcaoListas={handleAcaoListas}
          handleAcaoListaById={handleAcaoListaById}
          handleChangePage={(paramsPag) =>
            handleChangePage(paramsPag, tipoStatus)
          }
          listasSize={listaStatus.totalCount}
          {...(tabName && { tabName })}
          usuario={usuario}
          reportsItems={reportsItems}
          setReportItems={setReportItems}
          handleCheckBoxAllPagesReport={handleCheckBoxAllPagesReport}
          isCheckedAllPages={isCheckedAllPages}
          handleDropdownItemClick={handleDropdownItemClick}
          downloadFileOriginLot={downloadFileOriginLot}
        />
      );
    }
    return <EmptyContent />;
  }

  useEffect(() => {
    const newPaginationParams = produce(paginationParams, (draft) => {
      draft.pageSize = 10;
      draft.pageNumber = 1;
    });

    setPaginationParams(newPaginationParams);
    setReportItems([]);
    setIsCheckedAllPages(false);
  }, [status]);

  return <>{handleRender(listas.response, isPendentStatusTable())}</>;
}

const TableListaHeader = ({
  tabName,
  usuario,
  checkAll,
  handleCheckAll,
  isCheckedAllPages,
}) => {
  return (
    <TableRow>
      {tabName === 'Pendente' && isAdmin(usuario.role) ? (
        <TableHeader widthAuto>
          <Checkbox
            checked={checkAll}
            onClick={(e) => handleCheckAll(e)}
            height={18}
            width={18}
          />
        </TableHeader>
      ) : (
        <TableHeader widthAuto>
          <Checkbox
            checked={checkAll || isCheckedAllPages}
            onClick={(e) => handleCheckAll(e)}
            height={18}
            width={18}
          />
        </TableHeader>
      )}

      <TableHeader maxWidth={120} minWidth={100}>
        Lista
      </TableHeader>
      <TableHeader maxWidth={120} minWidth={100}>
        Contratante
      </TableHeader>
      <TableHeader maxWidth={121} minWidth={100}>
        Movimentação
      </TableHeader>
      <TableHeader maxWidth={120} minWidth={100}>
        Criação
      </TableHeader>
      <TableHeader maxWidth={120} minWidth={100}>
        Envio
      </TableHeader>
      <TableHeader maxWidth={120} minWidth={100}>
        Finalização
      </TableHeader>
      <TableHeader maxWidth={120} minWidth={100}>
        Status
      </TableHeader>
      {tabName === 'Pendente' && isAdmin(usuario.role) ? (
        <TableHeader maxWidth={120} minWidth={100}>
          Status RPA
        </TableHeader>
      ) : null}

      <TableHeader maxWidth={16} minWidth={16} />
    </TableRow>
  );
};

const TableLista = ({
  listas,
  pageData,
  setPaginationParams,
  paginationParams,
  handlePageChangeKeyDown,
  handleConfirmAction,
  tabName,
  handleAcaoListas,
  listasSize,
  usuario,
  handleChangePage,
  reportsItems,
  setReportItems,
  handleCheckBoxAllPagesReport,
  isCheckedAllPages,
  handleDropdownItemClick,
  downloadFileOriginLot,
}) => {
  const [checkAll, setCheckAll] = useState(false);
  const [listaRowsChecked, setListaRowsChecked] = useState([]);

  const handleCheckAll = () => {
    if (isCheckedAllPages) {
      return;
    }
    const allItems = listas.slice(
      0,
      Math.min(listas.length, pageData.pageSize * (0 + 1))
    );
    const filteredItems = allItems.filter(
      (item) =>
        !reportsItems.some(
          (selectedItem) => selectedItem.idLista === item.idLista
        )
    );
    setReportItems([...reportsItems, ...filteredItems]);
    setCheckAll(true);
  };

  const handleCheckAllChange = () => {
    if (checkAll) {
      handleUncheckAll();
    } else {
      handleCheckAll();
    }
  };

  const handleUncheckAll = () => {
    const startIndex = 0 * pageData.pageSize;
    const endIndex = startIndex + pageData.pageSize;
    const currentPageItems = listas.slice(startIndex, endIndex);

    const filteredItems = reportsItems.filter((selectedItem) => {
      return !currentPageItems.some(
        (item) => item.idLista === selectedItem.idLista
      );
    });

    setReportItems(filteredItems);
    setCheckAll(false);
  };

  const isAllPageItemsChecked = () => {
    const startIndex = 0 * pageData.pageSize;
    const endIndex = startIndex + pageData.pageSize;
    const currentPageItems = listas.slice(startIndex, endIndex);
    return currentPageItems.every((item) =>
      reportsItems.some((selectedItem) => selectedItem.idLista === item.idLista)
    );
  };

  useEffect(() => {
    setCheckAll(isAllPageItemsChecked());
  }, [reportsItems]);

  const optionsButtonRPA = [
    {
      onClickOption: () => handleDropdownItemClick('Relatório resumido'),
      text: 'Relatório resumido',
    },
    {
      onClickOption: () => handleDropdownItemClick('Relatório detalhado'),
      text: 'Relatório detalhado',
    },
  ];
  useEffect(() => {
    const newPaginationParams = produce(paginationParams, (draft) => {
      draft.pageNumber = pageData.pageNumber;
      draft.pageSize = pageData.pageSize;
      draft.totalPages = pageData.totalPages;

      return draft;
    });
    // setPaginationParams(newPaginationParams);
  }, []);

  return (
    <>
      <FadeIn height="auto" width="100%">
        {reportsItems.length > 0 &&
          tabName === 'Pendente' &&
          isAdmin(usuario.role) && (
            <FadeIn height="auto" width="100%">
              <SetAllLista
                tabName={tabName}
                listaRowsChecked={listaRowsChecked}
                handleAcaoListas={handleAcaoListas}
                handleConfirmAction={handleConfirmAction}
                paginationParams={paginationParams}
                totalSize={listasSize}
                marginLeft={20}
                reportsItems={reportsItems}
              />
            </FadeIn>
          )}
        {reportsItems.length > 0 ? (
          <FadeIn height="auto" width="100%">
            <WrapperFlex marginBottom={25} justifySpaceBetween alignCenter>
              <Alerts type="success">
                <span>
                  Você selecionou {reportsItems.length} de {pageData.totalCount}{' '}
                  itens para Relatórios.
                </span>
              </Alerts>
              <Button
                tipo="secondary"
                justifySpaceBetween
                alignCenter
                width={180}
                paddingTop={10}
                paddingRight={10}
                paddingBottom={10}
                paddingLeft={10}
                padding={10}
                onClick={() => {
                  setReportItems([]);
                }}
              >
                Limpar Seleção
              </Button>
            </WrapperFlex>
          </FadeIn>
        ) : null}
        <Table
          head={
            <TableListaHeader
              tabName={tabName}
              usuario={usuario}
              checkAll={checkAll}
              handleCheckAll={handleCheckAllChange}
              isCheckedAllPages={isCheckedAllPages}
            />
          }
        >
          {listas.map((lista) => (
            <TableContent
              key={lista.idLista}
              lista={lista}
              checkAll={checkAll}
              setListaRowsChecked={setListaRowsChecked}
              listaRowsChecked={listaRowsChecked}
              paginationParams={paginationParams}
              handleConfirmAction={handleConfirmAction}
              tabName={tabName}
              usuario={usuario}
              reportsItems={reportsItems}
              setReportItems={setReportItems}
              isCheckedAllPages={isCheckedAllPages}
              downloadFileOriginLot={downloadFileOriginLot}
            />
          ))}
        </Table>
      </FadeIn>
      <WrapperFlex fullWidth>
        <WrapperFlex fullWidth justifySpaceBetween>
          <WrapperFlex column gap={25} padding={10}>
            <WrapperFlex gap={15}>
              <Checkbox
                checked={isCheckedAllPages}
                onClick={(e) => handleCheckBoxAllPagesReport(e)}
                height={18}
                width={18}
              />
              <Text
                text={`Enviar os ${pageData.totalCount} Registros para Relatório?`}
              />
            </WrapperFlex>
            <Dropdown
              margin={0}
              options={optionsButtonRPA}
              tipo="button"
              height={40}
              background={theme.greenDefault}
              fontHeader="16px unimed_slabregular"
            >
              <MdFileDownload size="20" color={theme.whiteDefault} />
              Gerar relatório
            </Dropdown>
          </WrapperFlex>
          {pageData.totalCount > 10 && (
            <Pagination
              total={pageData.totalCount}
              paginationParams={paginationParams}
              setPaginationParams={setPaginationParams}
              callbackChangePage={handleChangePage}
              handlePageChangeKeyDown={handlePageChangeKeyDown}
            />
          )}
        </WrapperFlex>
      </WrapperFlex>
    </>
  );
};

const TableContent = ({
  lista,
  checkAll,
  setListaRowsChecked,
  listaRowsChecked = [],
  handleConfirmAction,
  tabName,
  usuario,
  reportsItems,
  setReportItems,
  paginationParams,
  isCheckedAllPages,
  downloadFileOriginLot,
}) => {
  const [hoverAction, setHoverAction] = useState(false);

  function handleMouseEnterHover(e) {
    setHoverAction(!hoverAction);
  }
  function handleMouseLeaveHover(e) {
    if (hoverAction) setHoverAction(!hoverAction);
  }

  function redirectToDetalheLista(listaArg) {
    const TIPO_ACAO_INATIVOS = 'Inativos';

    if (listaArg && listaArg?.dcTipoAcao === TIPO_ACAO_INATIVOS) {
      history.push(`/novo/listainativo/${listaArg?.idLista}/detalhe`);
    } else {
      window.location.replace(
        `${REACT_APP_MOVCAD_LEGACY}/CadastrarLista.aspx?idLista=${listaArg.idLista}`
      );
    }
  }

  const handleItemCheck = (item) => {
    if (isCheckedAllPages) {
      return;
    }
    const isCheckeed = reportsItems.some(
      (selectedItem) => selectedItem.idLista === item.idLista
    );
    if (isCheckeed) {
      setReportItems(
        reportsItems.filter(
          (selectedItem) => selectedItem.idLista !== item.idLista
        )
      );
    } else {
      setReportItems([...reportsItems, item]);
    }
  };
  const movimentacoes = lista.dcMovimentacoes
    ? lista.dcMovimentacoes.split('/').join(' / ')
    : lista.dcMovimentacoes;

  const listaIsFake = lista?.nmLista.endsWith('_TESTE') || false;
  return (
    <TableRow
      onMouseEnter={handleMouseEnterHover}
      onMouseLeave={handleMouseLeaveHover}
      key={lista.idLista}
    >
      <TableCell widthAuto key={lista.idLista}>
        <Checkbox
          checked={
            reportsItems.some(
              (selectedItem) => selectedItem.idLista === lista.idLista
            ) || isCheckedAllPages
          }
          onClick={(e) => handleItemCheck(lista)}
          height={18}
          width={18}
        />
      </TableCell>
      <TableCell
        onClick={() => redirectToDetalheLista(lista)}
        activeAction
        minWidth={100}
        maxWidth={100}
      >
        {lista.nmLista}
      </TableCell>
      <TableCell minWidth={100} maxWidth={100}>
        {lista.cdContratante} - {lista.nmContratante}
      </TableCell>
      <TableCell minWidth={137} maxWidth={137}>
        {movimentacoes}
      </TableCell>
      <TableCell minWidth={100} maxWidth={100}>
        {lista.dtInclusao
          ? format(new Date(lista.dtInclusao), 'dd/MM/yyyy HH:mm', {
              timeZone: 'America/Sao_Paulo',
            })
          : null}
      </TableCell>
      <TableCell minWidth={100} maxWidth={100}>
        {lista.dtEnvio
          ? format(new Date(lista.dtEnvio), 'dd/MM/yyyy HH:mm')
          : null}
      </TableCell>
      <TableCell minWidth={100} maxWidth={100}>
        {lista.dtVencimento
          ? format(new Date(lista.dtVencimento), 'dd/MM/yyyy HH:mm')
          : null}
      </TableCell>
      <TableCell minWidth={100} maxWidth={100}>
        {(hoverAction && tabName !== 'Pendente') ||
        (hoverAction && isContratante(usuario.role)) ? (
          <TableListaActions
            redirectToDetalheLista={() => redirectToDetalheLista(lista)}
            idLista={lista.idLista}
            listaStatus={
              StatusListaAPIEnum.returnNameFormatted[lista.idStatusLista]
            }
            handleConfirmAction={(idLista, status) =>
              handleConfirmAction(idLista, status, listaIsFake)
            }
            nomeLista={lista.nmLista}
            downloadFileOriginLot={downloadFileOriginLot}
          />
        ) : (
          StatusListaAPIEnum.returnNameFormatted[lista.idStatusLista]
        )}
      </TableCell>
      {tabName === 'Pendente' && isAdmin(usuario.role) ? (
        <TableCell minWidth={100} maxWidth={100}>
          {hoverAction &&
          lista.idStatusLista !== StatusListaAPIEnum.returnId.Registrada ? (
            <TableListaActions
              redirectToDetalheLista={() => redirectToDetalheLista(lista)}
              idLista={lista.idLista}
              listaStatus={
                StatusListaAPIEnum.returnNameFormatted[lista.idStatusLista]
              }
              listaStatusRPA={lista.btEnvioRpa}
              handleConfirmAction={(idLista, status) =>
                handleConfirmAction(idLista, status, listaIsFake)
              }
              tipoAcao={lista.dcTipoAcao}
              nomeLista={lista.nmLista}
              downloadFileOriginLot={downloadFileOriginLot}
            />
          ) : (
            <>{lista.btEnvioRpa ? lista.dcStatusRpa : '-'}</>
          )}
        </TableCell>
      ) : null}
      <TableCell>
        {lista.possuiSinalizacao && (
          <Tooltip
            background={theme.greenDefault}
            color={theme.white}
            text="Lista contém beneficiários excluídos com motivo de cancelamento “RN 412 – Cancelamento/Exclusão a Pedido”"
            position="left"
          >
            <HoverActions>
              <MdInfo size="17" color={theme.movYellowDefault} />
            </HoverActions>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
};

const EmptyContent = () => (
  <WrapperFlex fullHeight justifyCenter alignCenter>
    <ContainerEmptyContent>Nenhum registro localizado</ContainerEmptyContent>
  </WrapperFlex>
);

const SetAllLista = ({
  listaRowsChecked,
  handleConfirmAction,
  paginationParams,
  marginLeft,
  totalSize,
  reportsItems,
}) => {
  return (
    <WrapperFlex marginBottom={25} justifySpaceBetween alignCenter>
      <Alerts type="success">
        <span>
          Você selecionou {reportsItems.length} de{' '}
          {totalSize >= 10 ? paginationParams.pageSize : totalSize} itens.
        </span>
      </Alerts>
      <Button
        tipo="secondary"
        justifySpaceBetween
        alignCenter
        width={180}
        paddingTop={10}
        paddingRight={10}
        paddingBottom={10}
        paddingLeft={10}
        padding={10}
        marginLeft={marginLeft}
        onClick={() =>
          handleConfirmAction(
            reportsItems,
            StatusListaAPIEnum.returnNameFormatted[10].replace(/\s+/g, ''),
            reportsItems.some((cheked) => cheked?.nmLista.endsWith('_TESTE'))
          )
        }
      >
        <MdAdb size="20" stroke={theme.greenDefault} />
        enviar p/ RPA
      </Button>
    </WrapperFlex>
  );
};

const TableListaActions = ({
  redirectToDetalheLista,
  idLista,
  listaStatus,
  handleConfirmAction,
  listaStatusRPA,
  tipoAcao = null,
  nomeLista,
  downloadFileOriginLot,
}) => {
  const TIPO_INATIVOS = 'Inativos';
  const { usuario } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  return (
    <WrapperFlex alignCenter justifySpaceEvenly>
      <Tooltip
        background={theme.greenDefault}
        color={theme.bgWhite}
        text="editar"
        position="bottom"
      >
        <HoverActions onClick={() => redirectToDetalheLista()}>
          <ImPencil size="17" color={theme.colorGray} />
        </HoverActions>
      </Tooltip>

      {usuario.role === TipoPerfilUsuarioEnum.returnNameForName.Adm &&
      (nomeLista?.endsWith('L_TESTE') || nomeLista?.endsWith('L')) ? (
        <Tooltip
          background={theme.greenDefault}
          color={theme.bgWhite}
          text="Download Arquivo Importação"
          position="bottom"
        >
          <HoverActions onClick={() => downloadFileOriginLot(nomeLista)}>
            <MdFileDownload size="17" color={theme.colorGray} />
          </HoverActions>
        </Tooltip>
      ) : null}

      {listaStatus === 'Pendente de Processamento' &&
      tipoAcao &&
      tipoAcao !== TIPO_INATIVOS ? (
        <CanAccess
          checkRole={{
            Adm: TipoPerfilUsuarioEnum.returnNameForName.Adm,
          }}
          userRole={usuario.role}
        >
          <ActionPorStatus
            listaStatus={listaStatus}
            idLista={idLista}
            handleConfirmAction={handleConfirmAction}
            listaStatusRPA={listaStatusRPA}
          />
        </CanAccess>
      ) : null}
    </WrapperFlex>
  );
};

const ActionPorStatus = ({
  listaStatus,
  idLista,
  handleConfirmAction,
  listaStatusRPA,
}) => {
  const status = listaStatus.replace(/\s+/g, '');
  const IconPorStatus = {
    PendentedeProcessamento: {
      Text: !listaStatusRPA ? 'enviar para RPA' : 'cancelar envio para RPA',
      Icon: !listaStatusRPA ? (
        <MdAdb size="17" color={theme.colorGray} />
      ) : (
        <Icon size="23" color={theme.colorGray} icon="cancelOutline" />
      ),
      onClick: !listaStatusRPA
        ? () => handleConfirmAction(idLista, status)
        : () => handleConfirmAction(idLista, 'Cancelar'),
    },
  };

  function handleIconsPorStatus() {
    return IconPorStatus[status].Icon;
  }

  function handleToolTipTextPorStatus() {
    return IconPorStatus[status].Text;
  }

  return (
    <Tooltip
      background={theme.greenDefault}
      color={theme.bgWhite}
      text={handleToolTipTextPorStatus()}
      position="bottom"
    >
      <HoverActions
        data-tip={handleToolTipTextPorStatus()}
        onClick={IconPorStatus[status].onClick}
      >
        {handleIconsPorStatus()}
      </HoverActions>
    </Tooltip>
  );
};
export default TabTableLista;
